import { useCallback, useContext, useState } from "react";
import { AIContext } from "contexts/AIContext";

/**
 * Shared logic for every MagicEditor AI action: run an instruction-style prompt
 * (optionally with a structured-output schema) and write the clean result back
 * into the editor. Replaces the prompt+getByQueryChat+selection-splice code that
 * used to be duplicated across ~25 function files.
 *
 * getByQueryChat returns a parsed object when a schema is passed (e.g.
 * { result } or { items }), and a cleaned string otherwise — so callers should
 * read it defensively with the asText / asItems helpers below.
 */

// Single transformed string. Used by the bulk of the actions.
export const RESULT_SCHEMA = {
    name: "magic_text",
    strict: true,
    schema: {
        type: "object",
        additionalProperties: false,
        properties: {
            result: { type: "string", description: "The transformed text, and nothing else." },
        },
        required: ["result"],
    },
};

// A list of short strings (hashtags, emojis, ideas).
export const ITEMS_SCHEMA = (description) => ({
    name: "magic_items",
    strict: true,
    schema: {
        type: "object",
        additionalProperties: false,
        properties: {
            items: {
                type: "array",
                items: { type: "string", description: description || "An item." },
            },
        },
        required: ["items"],
    },
});

// Read a RESULT_SCHEMA response whether it came back as an object or a string.
export const asText = (r) => (typeof r === "string" ? r : (r?.result ?? "")).trim();

// Read an ITEMS_SCHEMA response, tolerating string fallbacks (split on lines/spaces).
// The non-schema-driver fallback (getByQueryChat) returns { items: "<string>" } —
// handle that too so hashtags/emojis/ideas aren't silently dropped on non-schema drivers.
export const asItems = (r) => {
    if (Array.isArray(r?.items)) return r.items.filter((x) => typeof x === "string" && x.trim());
    if (typeof r?.items === "string") return asItems(r.items);
    if (Array.isArray(r)) return r.filter((x) => typeof x === "string" && x.trim());
    if (typeof r === "string") return r.split(/\r?\n|\s{2,}/).map((s) => s.trim()).filter(Boolean);
    return [];
};

const useMagicAction = ({ value, fullText, selectedText, selectionStartEnd, setValue, setMenuOpened }) => {
    const { getByQueryChat } = useContext(AIContext);
    const [isLoading, setIsLoading] = useState(false);

    // INPUT for transform actions: the selection if present, else the whole text.
    const input = selectedText ? selectedText : fullText;

    const run = useCallback(
        async ({ prompt, schema, system }) => {
            setIsLoading(true);
            try {
                return await getByQueryChat(
                    prompt,
                    /* isChargable */ true,
                    /* forceNewQuerySystem */ false,
                    /* newQuerySystem */ system || "",
                    /* aiResultId */ undefined,
                    /* config */ {},
                    /* groupId */ undefined,
                    /* _model */ undefined,
                    /* jsonSchema */ schema
                );
            } finally {
                setIsLoading(false);
            }
        },
        [getByQueryChat]
    );

    // Replace the selection (or the whole field when nothing is selected).
    const writeBackReplace = useCallback(
        (resultText) => {
            const left = selectedText ? String(value)?.substring(0, selectionStartEnd.start) : "";
            const right = selectedText ? String(value)?.substring(selectionStartEnd.finish, String(value)?.length) : "";
            setValue(`${left}${resultText}${right}`);
        },
        [value, selectedText, selectionStartEnd, setValue]
    );

    // Insert at the cursor/selection without dropping the surrounding text.
    const writeBackInsert = useCallback(
        (resultText) => {
            const left = String(value)?.substring(0, selectionStartEnd.start);
            const right = String(value)?.substring(selectionStartEnd.finish, String(value)?.length);
            setValue(`${left}${resultText}${right}`);
        },
        [value, selectionStartEnd, setValue]
    );

    // Overwrite the entire field.
    const writeBackAll = useCallback((resultText) => setValue(`${resultText}`), [setValue]);

    const closeMenu = useCallback(() => setMenuOpened(false), [setMenuOpened]);

    return { isLoading, input, run, writeBackReplace, writeBackInsert, writeBackAll, closeMenu };
};

export default useMagicAction;
